/**
 * PRODUCT_DEV-1238 メールの無い人のパスワード再発行（ログイン画面側）の操作。
 *
 * - 依頼は「パスワードを忘れた」の送信（useLoginHandlers）がそのまま行う（案C・確認の画面は無い）
 * - コード: 行員番号かメールアドレス・8文字のコード・新しいパスワード。成功したらログイン画面へ戻し、
 *   新しいパスワードで普通にログインしてもらう（MFA を飛ばさない）
 */
import type React from "react";
import { validatePasswordRequirements } from "@/utils/passwordValidation";
import { PasswordReissueRedeemError, redeemPasswordReissue } from "@/lib/passwordReissueApi";
import {
  buildReissueInvalidMessage,
  REISSUE_CODE_LENGTH,
  REISSUE_DONE_MESSAGE,
} from "../resetPasswordTarget";
import type { useLoginState } from "./useLoginState";

type LoginState = ReturnType<typeof useLoginState>;

export const useReissueHandlers = (
  state: LoginState,
  aliasTenantId: string | null,
  /** 会社の ID の呼び方（例「行員番号」）。エラーの文に使う。 */
  loginIdLabel: string | null = null,
) => {
  const clearMessages = () => {
    state.setError(null);
    state.setRecoverySuggestion(null);
  };

  const clearReissueInputs = () => {
    state.setReissueCode("");
    state.setResetPassword("");
    state.setResetConfirmPassword("");
  };

  /** 「管理者からコードを受け取った方」 */
  const handleOpenReissueCode = () => {
    clearMessages();
    clearReissueInputs();
    state.setResetFlowStep("reissueCode");
  };

  /** コードで新しいパスワードを決める */
  const handleReissueRedeem = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    const loginId = state.resetEmail.trim();
    const code = state.reissueCode.trim();
    if (!loginId) {
      state.setError(`${loginIdLabel ?? "エイリアス"}またはメールアドレスを入力してください。`);
      return;
    }
    if (code.length !== REISSUE_CODE_LENGTH) {
      state.setError(`${REISSUE_CODE_LENGTH}文字のコードを入力してください。`);
      return;
    }
    if (state.resetPassword !== state.resetConfirmPassword) {
      state.setError("パスワードが一致しません。");
      return;
    }
    const passwordError = validatePasswordRequirements(state.resetPassword);
    if (passwordError) {
      state.setError(passwordError);
      return;
    }
    if (!aliasTenantId) return;

    state.setLoading(true);
    state.setLoadingMessage("パスワードを設定しています...");
    try {
      await redeemPasswordReissue({
        tenantId: aliasTenantId,
        loginId,
        code,
        newPassword: state.resetPassword,
      });
      state.setEmail(loginId);
      state.setResetFlowStep(null);
      state.setResetEmail("");
      state.setResetUsername("");
      clearReissueInputs();
      state.setError(null);
      state.setRecoverySuggestion(REISSUE_DONE_MESSAGE);
    } catch (err) {
      state.setError(
        err instanceof PasswordReissueRedeemError && err.reason === "invalid"
          ? buildReissueInvalidMessage(loginIdLabel)
          : err instanceof Error
            ? err.message
            : String(err),
      );
    } finally {
      state.setLoading(false);
      state.setLoadingMessage("");
    }
  };

  /** ログイン画面へ戻る（依頼の流れのどこからでも） */
  const handleReissueBackToLogin = () => {
    state.setResetFlowStep(null);
    state.setResetEmail("");
    state.setResetUsername("");
    clearReissueInputs();
    clearMessages();
  };

  return {
    handleOpenReissueCode,
    handleReissueRedeem,
    handleReissueBackToLogin,
  };
};
